import React, { useState } from 'react';
import { UserIcon, BookIcon, SaveIcon, PaletteIcon, MapPinIcon, GlobeIcon, InstagramIcon, TwitterIcon, MailIcon, ShieldIcon, BellIcon, CheckIcon } from 'lucide-react';
interface SocialPlatform {
  platform: string;
  url: string;
  logo: string;
}
interface ReaderProfileData {
  name: string;
  username: string;
  email: string;
  bio: string;
  location: string;
  website: string;
  profileImage: File | null;
  bannerImage: File | null;
  instagram: string;
  twitter: string;
  tiktok: string;
  include: string[];
  exclude: string[];
  readingGoal: number;
  privacySettings: {
    showReadingStatus: boolean;
    showReviews: boolean;
    showLibrary: boolean;
  };
  notificationPreferences: {
    newReleases: boolean;
    authorUpdates: boolean;
    readingChallenges: boolean;
    bookClubUpdates: boolean;
  };
  socialLinks: SocialPlatform[];
}
const bookTopicsDB = ['Romance', 'Fantasy', 'Enemies to Lovers', 'Found Family', 'Violence', 'Gore', 'Slow Burn', 'Love Triangle', 'Thriller', 'Science Fiction', 'Drug Use', 'Strong Language', 'Historical Fiction'];
const ReaderProfileSettings = () => {
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [newPlatform, setNewPlatform] = useState('');
  const [showPlatformOptions, setShowPlatformOptions] = useState(false);
  const platformOptions = [{
    name: 'Personal Website',
    logo: '/placeholder-logo.png'
  }, {
    name: 'Instagram',
    logo: 'https://logo.clearbit.com/instagram.com'
  }, {
    name: 'Twitter',
    logo: 'https://logo.clearbit.com/twitter.com'
  }, {
    name: 'TikTok',
    logo: 'https://logo.clearbit.com/tiktok.com'
  }, {
    name: 'YouTube',
    logo: 'https://logo.clearbit.com/youtube.com'
  }, {
    name: 'Facebook',
    logo: 'https://logo.clearbit.com/facebook.com'
  }, {
    name: 'LinkedIn',
    logo: 'https://logo.clearbit.com/linkedin.com'
  }, {
    name: 'Goodreads',
    logo: 'https://logo.clearbit.com/goodreads.com'
  }];
  const [inputValue, setInputValue] = useState('');
  const [activeList, setActiveList] = useState<'include' | 'exclude'>('include');
  const [formData, setFormData] = useState<ReaderProfileData>({
    name: '',
    username: '',
    email: '',
    bio: '',
    location: '',
    website: '',
    profileImage: null,
    bannerImage: null,
    instagram: '',
    twitter: '',
    tiktok: '',
    include: [],
    exclude: [],
    readingGoal: 12,
    privacySettings: {
      showReadingStatus: true,
      showReviews: true,
      showLibrary: true
    },
    notificationPreferences: {
      newReleases: true,
      authorUpdates: true,
      readingChallenges: true,
      bookClubUpdates: true
    },
    socialLinks: [{
      platform: 'Personal Website',
      url: '',
      logo: '/placeholder-logo.png'
    }]
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      files
    } = e.target;
    if (files && files[0]) {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  const handleAdd = (topic: string, type: 'include' | 'exclude') => {
    if (!formData[type].includes(topic)) {
      setFormData({
        ...formData,
        [type]: [...formData[type], topic]
      });
    }
    setInputValue('');
  };
  const handleRemove = (topic: string, type: 'include' | 'exclude') => {
    setFormData({
      ...formData,
      [type]: formData[type].filter((item: string) => item !== topic)
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleAdd(inputValue.trim(), activeList);
      e.preventDefault();
    }
  };
  const filteredSuggestions = bookTopicsDB.filter(topic => topic.toLowerCase().includes(inputValue.toLowerCase()) && !formData.include.includes(topic) && !formData.exclude.includes(topic));
  return <form onSubmit={handleSubmit} className="space-y-6" data-id="element-12538" data-mp="293cnvz9dokh6">
      {/* Basic Information */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12539" data-mp="2e1ny4b3l4wzx">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12540" data-mp="2itxizcpuf6vc">
          <UserIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12541" data-mp="2iw1m3sfx41uk" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12542" data-mp="2mx3q0mks522v">
          <div data-id="element-12543" data-mp="2rvx3if02gt31">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12544" data-mp="2rs97n975ful5">
              Display Name
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required data-id="element-12545" data-mp="36yyg2xtlabqj" />
          </div>
          <div data-id="element-12546" data-mp="3poek3v4hgmbp">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12547" data-mp="3pmtoe26fmxc9">
              Username
            </label>
            <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" required data-id="element-12548" data-mp="3fxjvwl7ofcnm" />
          </div>
          <div className="md:col-span-2" data-id="element-12549" data-mp="7gfbm6jwgtfg">
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12550" data-mp="21pua4zyzg6ci">
              Bio
            </label>
            <textarea id="bio" name="bio" value={formData.bio} onChange={handleInputChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-12551" data-mp="1s0aojmjvxydy" />
          </div>
          <div data-id="element-12552" data-mp="1nyz5jzviobeq">
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12553" data-mp="1nzh0mt6g0jl2">
              Location
            </label>
            <input type="text" id="location" name="location" value={formData.location} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-12554" data-mp="1dzq8gy5cugo5" />
          </div>
          <div data-id="element-12555" data-mp="wp4hr78ofa7b">
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-12556" data-mp="rs4ait03c1em">
              Website
            </label>
            <input type="url" id="website" name="website" value={formData.website} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-12557" data-mp="16j3etrtw83se" />
          </div>
        </div>
      </div>

      {/* Profile Images */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12558" data-mp="1pf3773qs99ci">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12559" data-mp="1kgi5t0zsojmh">
          <PaletteIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12560" data-mp="3r6prynk3080j" />
          Profile Images
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12561" data-mp="3m8o840eu04o0">
          <div data-id="element-12562" data-mp="3mahi9ponl70r">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12563" data-mp="30f8vdvnrltfp">
              Profile Picture
            </label>
            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-12564" data-mp="35dagsn8ny4uc">
              <div className="space-y-1 text-center" data-id="element-12565" data-mp="3a63ggwp9ymzk">
                <div className="flex flex-col items-center" data-id="element-12566" data-mp="3f3luh1i5fsef">
                  {formData.profileImage ? <img src={URL.createObjectURL(formData.profileImage)} alt="Profile preview" className="h-28 w-28 rounded-full object-cover mb-4 shadow-sm" data-id="element-12567" data-mp="2hgmtwfoc60b9" /> : <div className="h-28 w-28 rounded-full bg-indigo-100 flex items-center justify-center mb-4" data-id="element-12568" data-mp="2rgnh4cpbt0kq">
                      <UserIcon className="h-14 w-14 text-indigo-400" data-id="element-12569" data-mp="6o21m3le4bzw" />
                    </div>}
                  <div className="flex text-sm text-gray-600" data-id="element-12570" data-mp="bmn469o477ri">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-12571" data-mp="bozlckm1te3i">
                      <span data-id="element-12572" data-mp="1meuovmjpyrnh">Upload a file</span>
                      <input type="file" name="profileImage" className="sr-only" onChange={handleFileChange} accept="image/*" data-id="element-12573" data-mp="1mdjl3mq9xjwd" />
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-12574" data-mp="13ga1a4h1e4el">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </div>
          </div>
          <div data-id="element-12575" data-mp="yjjfr1mgqgls">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12576" data-mp="2xuj6rzi490hf">
              Banner Image
            </label>
            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-12577" data-mp="2sv6n1nmjk9j2">
              <div className="space-y-1 text-center" data-id="element-12578" data-mp="msjfb9g3bjzn">
                <div className="flex flex-col items-center" data-id="element-12579" data-mp="huhykv64zwrc">
                  {formData.bannerImage ? <img src={URL.createObjectURL(formData.bannerImage)} alt="Banner preview" className="h-28 w-full object-cover mb-4 rounded-md shadow-sm" data-id="element-12580" data-mp="vu0wkv58hrpx" /> : <div className="h-28 w-full bg-gradient-to-r from-indigo-400 via-pink-500 to-red-500 rounded-md flex items-center justify-center mb-4" data-id="element-12581" data-mp="410gzfx0gu3y">
                      <PaletteIcon className="h-14 w-14 text-white/70" data-id="element-12582" data-mp="cwghgfs9u7ii" />
                    </div>}
                  <div className="flex text-sm text-gray-600" data-id="element-12583" data-mp="83wuwtblw21k">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-12584" data-mp="3gjzz2p3if8l5">
                      <span data-id="element-12585" data-mp="32devfdm9jp2m">Upload a file</span>
                      <input type="file" name="bannerImage" className="sr-only" onChange={handleFileChange} accept="image/*" data-id="element-12586" data-mp="375g30m8hkrmb" />
                    </label>
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-12587" data-mp="3l6sjqzvh0x99">
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Media Links */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12588" data-mp="3b71jsio55q1s">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12589" data-mp="3g5l82pq7jdxb">
          <MailIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12590" data-mp="1zrmsnvim5sel" />
          Social Media
        </h3>
        <div className="space-y-4" data-id="element-12591" data-mp="24rqy8qhzh2q1">
          {formData.socialLinks.sort((a, b) => {
          // Keep Personal Website at the top, sort others alphabetically
          if (a.platform === 'Personal Website') return -1;
          if (b.platform === 'Personal Website') return 1;
          return a.platform.localeCompare(b.platform);
        }).map((link, index) => <div key={index} className="flex items-center gap-2" data-id="element-12592" data-mp="3spry2r42rylr" data-mpi={index}>
                <div className="flex items-center w-32" data-id="element-12593" data-mp="3npx76nkav90p">
                  <img src={link.logo} alt={link.platform} className="h-5 w-5 mr-2 object-contain" onError={e => {
              e.currentTarget.src = '/placeholder-logo.png';
            }} data-id="element-12594" data-mp="3nrgq6lxcby66" />
                  <label className="block text-sm font-medium text-gray-700" data-id="element-12595" data-mp="39rywpt93nwqh">
                    {link.platform}
                  </label>
                </div>
                <input type="url" className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" placeholder={`${link.platform} URL`} value={link.url} onChange={e => {
            const updatedLinks = [...formData.socialLinks];
            updatedLinks[index].url = e.target.value;
            setFormData({
              ...formData,
              socialLinks: updatedLinks
            });
          }} data-id="element-12596" data-mp="17yhpgqovsxw9" />
                {link.platform !== 'Personal Website' && <button type="button" onClick={() => {
            const updatedLinks = formData.socialLinks.filter((_, i) => i !== index);
            setFormData({
              ...formData,
              socialLinks: updatedLinks
            });
          }} className="text-red-500 hover:text-red-700 p-1" data-id="element-12597" data-mp="16hlvckvbeicf">
                    ×
                  </button>}
              </div>)}
          {/* Add New Platform */}
          <div className="flex gap-2 items-center mt-4 pt-2 border-t border-gray-200" data-id="element-12598" data-mp="2ugei77wxu3mt">
            <div className="relative w-full" data-id="element-12599" data-mp="2pfqqek0vxirj">
              <input type="text" value={newPlatform} onChange={e => {
              setNewPlatform(e.target.value);
              setShowPlatformOptions(true);
            }} onFocus={() => setShowPlatformOptions(true)} onBlur={() => setTimeout(() => setShowPlatformOptions(false), 200)} className="w-full rounded-md border border-gray-300 py-1.5 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Add another platform (Instagram, Twitter, etc.)" data-id="element-12600" data-mp="2peywrequlv3w" />
              {showPlatformOptions && <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm" data-id="element-12601" data-mp="5j8t2sheb5hb">
                  {/* Show existing platforms that match search */}
                  {formData.socialLinks.filter(existing => existing.platform.toLowerCase().includes(newPlatform.toLowerCase()) && existing.platform !== 'Personal Website').map((existing, idx) => <div key={`existing-${idx}`} className="px-4 py-2 text-gray-500 cursor-not-allowed flex items-center" data-id="element-12602" data-mp="3e0dg3ewk42t6" data-mpi={idx}>
                        <img src={existing.logo} alt={existing.platform} className="h-4 w-4 mr-2 object-contain" onError={e => {
                  e.currentTarget.src = '/placeholder-logo.png';
                }} data-id="element-12603" data-mp="3438qf53i5p7m" />
                        {existing.platform} (already added)
                      </div>)}
                  {/* Show available platforms */}
                  {platformOptions.filter(p => p.name.toLowerCase().includes(newPlatform.toLowerCase()) && !formData.socialLinks.some(existing => existing.platform === p.name)).sort((a, b) => a.name.localeCompare(b.name)).map((platform, idx) => <div key={idx} onClick={() => {
                setFormData({
                  ...formData,
                  socialLinks: [...formData.socialLinks, {
                    platform: platform.name,
                    url: '',
                    logo: platform.logo
                  }]
                });
                setNewPlatform('');
                setShowPlatformOptions(false);
              }} className="cursor-pointer px-4 py-2 hover:bg-indigo-100 flex items-center" data-id="element-12604" data-mp="1djharc0uruuq" data-mpi={idx}>
                        <img src={platform.logo} alt={platform.name} className="h-4 w-4 mr-2 object-contain" onError={e => {
                  e.currentTarget.src = '/placeholder-logo.png';
                }} data-id="element-12605" data-mp="qofhdqme600u" />
                        {platform.name}
                      </div>)}
                  {/* Show custom option if no matches */}
                  {newPlatform && !platformOptions.some(p => p.name.toLowerCase().includes(newPlatform.toLowerCase())) && !formData.socialLinks.some(existing => existing.platform.toLowerCase().includes(newPlatform.toLowerCase())) && <div onClick={() => {
                setFormData({
                  ...formData,
                  socialLinks: [...formData.socialLinks, {
                    platform: newPlatform,
                    url: '',
                    logo: `https://logo.clearbit.com/${newPlatform.toLowerCase().replace(/\s+/g, '')}.com`
                  }]
                });
                setNewPlatform('');
                setShowPlatformOptions(false);
              }} className="cursor-pointer px-4 py-2 hover:bg-indigo-100 flex items-center text-indigo-600" data-id="element-12606" data-mp="kajev38i65mq">
                        <span className="h-4 w-4 mr-2 flex items-center justify-center text-xs" data-id="element-12607" data-mp="2qxqsccgj72xe">
                          +
                        </span>
                        Add "{newPlatform}" as custom platform
                      </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Reading Preferences */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12608" data-mp="39qkxyfs9lioy">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12609" data-mp="1546qtd1zc61l">
          <BookIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12610" data-mp="1a3tcmavvium0" />
          Reading Preferences
        </h3>
        <div className="space-y-6" data-id="element-12611" data-mp="vf6nxikcwqi4">
          {/* Content Preferences */}
          <div data-id="element-12612" data-mp="vcu758qc4m6n">
            <div className="flex gap-4 mb-4" data-id="element-12613" data-mp="10e02zffakpke">
              <button type="button" className={`px-4 py-2 rounded-md text-sm font-medium ${activeList === 'include' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-gray-100 text-gray-600'}`} onClick={() => setActiveList('include')} data-id="element-12614" data-mp="10cozlgprdjtt">
                I want to see
              </button>
              <button type="button" className={`px-4 py-2 rounded-md text-sm font-medium ${activeList === 'exclude' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-gray-100 text-gray-600'}`} onClick={() => setActiveList('exclude')} data-id="element-12615" data-mp="hew0kpi99u1p">
                I don't want to see
              </button>
            </div>
            <input type="text" placeholder="Search book topics..." value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} className={`w-full border px-3 py-2 text-sm rounded-md mb-2 focus:outline-none ${activeList === 'include' ? 'border-green-300 focus:ring-green-500' : 'border-red-300 focus:ring-red-500'}`} data-id="element-12616" data-mp="3rc4izy23at2w" />
            {/* Suggestions Dropdown */}
            {inputValue && <ul className="bg-white border border-gray-200 rounded-md mt-1 max-h-60 overflow-auto" data-id="element-12617" data-mp="sv0lxgfaza47">
                {filteredSuggestions.map(topic => <li key={topic} className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => handleAdd(topic, activeList)} data-id="element-12618" data-mp="ntb8ju4lykcy">
                    {topic}
                  </li>)}
                {!filteredSuggestions.length && inputValue.trim() && <li className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm text-indigo-600" onClick={() => handleAdd(inputValue.trim(), activeList)} data-id="element-12619" data-mp="yjqq76y20nqr">
                    Add "{inputValue.trim()}"
                  </li>}
              </ul>}
            {/* Selected Topics */}
            <div className="mt-4 space-y-4" data-id="element-12620" data-mp="1hhbjqrbrkahp">
              {/* Included Topics */}
              {formData.include.length > 0 && <div data-id="element-12621" data-mp="1m9l3fpmacebj">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-12622" data-mp="1m9394yj6oa69">
                    Topics I want to see:
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-12623" data-mp="1v987ykaba08t">
                    {formData.include.map(topic => <span key={topic} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 border border-green-300" data-id="element-12624" data-mp="2079ooyk9lnh4">
                        {topic}
                        <button type="button" onClick={() => handleRemove(topic, 'include')} className="ml-2 text-green-600 hover:text-green-800" data-id="element-12625" data-mp="25f9f8uet6k69">
                          ×
                        </button>
                      </span>)}
                  </div>
                </div>}
              {/* Excluded Topics */}
              {formData.exclude.length > 0 && <div data-id="element-12626" data-mp="2fb2nw2peptir">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-12627" data-mp="2fbm5fib5afw9">
                    Topics I don't want to see:
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-12628" data-mp="3bua3sf1i802t">
                    {formData.exclude.map(topic => <span key={topic} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 border border-red-300" data-id="element-12629" data-mp="370fkvdtq3p4s">
                        {topic}
                        <button type="button" onClick={() => handleRemove(topic, 'exclude')} className="ml-2 text-red-600 hover:text-red-800" data-id="element-12630" data-mp="2ikvw8zccgf6i">
                          ×
                        </button>
                      </span>)}
                  </div>
                </div>}
            </div>
          </div>
          {/* Annual Reading Goal - keeping this from the original */}
          <div data-id="element-12631" data-mp="35i78s19c9drf">
            <label htmlFor="readingGoal" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12632" data-mp="35hxjkfw4bams">
              Annual Reading Goal
            </label>
            <input type="number" id="readingGoal" name="readingGoal" min="1" value={formData.readingGoal} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-12633" data-mp="1x1k9v97zetp" />
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12634" data-mp="13lycruy1bkll">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12635" data-mp="13okjjsd3pw2r">
          <ShieldIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12636" data-mp="yoy7vncx8nw2" />
          Privacy Settings
        </h3>
        <div className="space-y-4" data-id="element-12637" data-mp="1dm8o1a0iehy4">
          {Object.entries(formData.privacySettings).map(([key, value]) => <div key={key} className="flex items-center justify-between" data-id="element-12638" data-mp="18nns3msaikfh">
              <span className="text-sm text-gray-700" data-id="element-12639" data-mp="2oy9jf7rvp4mu">
                {key === 'showReadingStatus' ? 'Show my reading status' : key === 'showReviews' ? 'Show my reviews' : 'Show my library'}
              </span>
              <button type="button" onClick={() => setFormData({
            ...formData,
            privacySettings: {
              ...formData.privacySettings,
              [key]: !value
            }
          })} className={`${value ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`} data-id="element-12640" data-mp="2w1pujrvgw64l">
                <span aria-hidden="true" className={`${value ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-12641" data-mp="2n45tok1iobui" />
              </button>
            </div>)}
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white rounded-lg shadow-md p-6" data-id="element-12642" data-mp="7n7ssqeswe65">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-12643" data-mp="2p63hnqs7im8">
          <BellIcon className="h-5 w-5 text-indigo-600 mr-2" data-id="element-12644" data-mp="3txboauvbu5g2" />
          Notification Preferences
        </h3>
        <div className="space-y-4" data-id="element-12645" data-mp="3oyh5vu19uiwh">
          {Object.entries(formData.notificationPreferences).map(([key, value]) => <div key={key} className="flex items-center justify-between" data-id="element-12646" data-mp="3k4me03biixmb">
                <span className="text-sm text-gray-700" data-id="element-12647" data-mp="3g5ncksozpizm">
                  {key === 'newReleases' ? 'New releases from favorite authors' : key === 'authorUpdates' ? 'Author updates and announcements' : key === 'readingChallenges' ? 'Reading challenge updates' : 'Book club updates'}
                </span>
                <button type="button" onClick={() => setFormData({
            ...formData,
            notificationPreferences: {
              ...formData.notificationPreferences,
              [key]: !value
            }
          })} className={`${value ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`} data-id="element-12648" data-mp="xy9is6oh6pfp">
                  <span aria-hidden="true" className={`${value ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-12649" data-mp="64zed76y1qpm" />
                </button>
              </div>)}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end" data-id="element-12650" data-mp="34t9ekb91jpko">
        <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12651" data-mp="3iqxyngvaasoo">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-12652" data-mp="3nqkdtvuoaj04" />
          Save Changes
        </button>
      </div>

      {saved && <div className="fixed bottom-4 right-4 flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-md shadow-lg" data-id="element-12653" data-mp="5agijrynrs1s">
          <CheckIcon className="h-5 w-5 mr-2" data-id="element-12654" data-mp="dglg6jsdzmt" />
          Changes saved successfully
        </div>}
    </form>;
};
export default ReaderProfileSettings;